import { isAxiosError } from 'axios';
import {
  V1ContentMatchSource,
  V1ContentSkipReason,
  V1EmailPart,
  type V1ContentLocation,
  type V1FileContentMatch,
  type V1ListRunsResponse,
  type V1Run,
  type V1RunContentCoverage,
  type V1RunContentMatch,
} from '@/generated/hooks/jeeves/models';
import i18n from '@/shared/i18n';
import { formatDisplayName } from '@/shared/utils';

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, { ns: 'automation', ...options });

/** Content matches of the loaded runs, keyed by run name. */
export type TRunContentMatches = Record<string, V1RunContentMatch>;

/**
 * Collects `content_matches` from every loaded page of a ListRuns response.
 * Each page keys its matches by run name, and a run appears on one page only.
 *
 * @param pages - The loaded ListRuns pages
 * @returns The matches of every loaded run, keyed by run name
 */
export function mergeContentMatches(
  pages: ReadonlyArray<{ data: V1ListRunsResponse }> | undefined,
): TRunContentMatches {
  const merged: TRunContentMatches = {};
  for (const page of pages ?? []) {
    Object.assign(merged, page.data.content_matches);
  }
  return merged;
}

const VALUE_SOURCES: ReadonlySet<string> = new Set([
  V1ContentMatchSource.CONTENT_MATCH_SOURCE_INPUT,
  V1ContentMatchSource.CONTENT_MATCH_SOURCE_OUTPUT,
  V1ContentMatchSource.CONTENT_MATCH_SOURCE_TRIGGER,
]);

const SOURCE_LABEL_KEYS: Record<string, string> = {
  [V1ContentMatchSource.CONTENT_MATCH_SOURCE_INPUT]:
    'automation.runs.contentSearch.source.input',
  [V1ContentMatchSource.CONTENT_MATCH_SOURCE_OUTPUT]:
    'automation.runs.contentSearch.source.output',
  [V1ContentMatchSource.CONTENT_MATCH_SOURCE_TRIGGER]:
    'automation.runs.contentSearch.source.trigger',
  [V1ContentMatchSource.CONTENT_MATCH_SOURCE_STEP_FILE]:
    'automation.runs.contentSearch.source.stepFile',
  [V1ContentMatchSource.CONTENT_MATCH_SOURCE_STEP_OUTPUT]:
    'automation.runs.contentSearch.source.stepOutput',
};

/**
 * Where in the run a matched file was found, as a short label.
 *
 * @param source - The match's source; newer servers may send values this build doesn't know
 * @returns "Input", "Output", "Trigger", "Step file", "Step output", or "Other"
 */
export function getContentMatchSourceLabel(source?: string): string {
  const key = source ? SOURCE_LABEL_KEYS[source] : undefined;
  return t(key ?? 'automation.runs.contentSearch.source.other');
}

/**
 * The name to show for a matched file: its file name, else the value it sits
 * in, else the step that produced it.
 *
 * @param match - One matched file
 * @returns A display name; never empty
 */
export function getFileMatchName(match: V1FileContentMatch): string {
  if (match.display_name) return match.display_name;
  if (match.field) return formatDisplayName(match.field);
  if (match.step_label) return match.step_label;
  return match.source === V1ContentMatchSource.CONTENT_MATCH_SOURCE_STEP_OUTPUT
    ? t('automation.runs.contentSearch.source.stepOutput')
    : t('automation.runs.contentSearch.unnamedFile');
}

/**
 * The part of the run a matched file belongs to, when the name doesn't
 * already say it: the value a named file sits in, or the step that used it.
 *
 * @param match - One matched file
 * @returns The value or step name, or undefined when there is nothing to add
 */
export function getFileMatchContext(
  match: V1FileContentMatch,
): string | undefined {
  const name = getFileMatchName(match);
  const context = VALUE_SOURCES.has(match.source ?? '')
    ? match.field && formatDisplayName(match.field)
    : match.step_label;
  return context && context !== name ? context : undefined;
}

const EMAIL_PART_LABEL_KEYS: Record<string, string> = {
  [V1EmailPart.EMAIL_PART_SUBJECT]:
    'automation.runs.contentSearch.location.emailSubject',
  [V1EmailPart.EMAIL_PART_HEADERS]:
    'automation.runs.contentSearch.location.emailHeaders',
  [V1EmailPart.EMAIL_PART_BODY]:
    'automation.runs.contentSearch.location.emailBody',
};

/**
 * Where in a file the term was found: "Page 3", "Sheet Q3", "Email subject"
 * or "Attachment remit.pdf".
 *
 * @param location - One location of a match
 * @returns The label, or undefined for a location this build can't name
 */
export function formatContentLocation(
  location: V1ContentLocation,
): string | undefined {
  if (location.page_number) {
    return t('automation.runs.contentSearch.location.page', {
      page: location.page_number,
    });
  }
  if (location.sheet) {
    return t('automation.runs.contentSearch.location.sheet', {
      sheet: location.sheet,
    });
  }
  if (location.email_attachment) {
    return t('automation.runs.contentSearch.location.attachment', {
      name: location.email_attachment,
    });
  }
  const partKey = location.email_part
    ? EMAIL_PART_LABEL_KEYS[location.email_part]
    : undefined;
  return partKey ? t(partKey) : undefined;
}

/**
 * The locations of a matched file as labels, in document order.
 *
 * @param match - One matched file
 * @returns Labels for the locations this build can name
 */
export function getFileMatchLocations(match: V1FileContentMatch): string[] {
  return (match.locations ?? [])
    .map(formatContentLocation)
    .filter((label): label is string => Boolean(label));
}

/**
 * How many matched files the server left out of `file_matches`.
 *
 * @param match - A run's content match
 * @returns The number of files not listed, 0 when all are
 */
export function getHiddenFileMatchCount(match: V1RunContentMatch): number {
  const shown = match.file_matches?.length ?? 0;
  return Math.max(0, (match.file_match_count ?? shown) - shown);
}

/**
 * Gives each listed file a stable React key. A file can be listed twice (an
 * input a step also read), so repeats of the same file get their own count.
 *
 * @param files - A run's `file_matches`
 * @returns Each file with its key, in order
 */
export function keyFileMatches(
  files: readonly V1FileContentMatch[] | undefined,
): { file: V1FileContentMatch; key: string }[] {
  const seen = new Map<string, number>();
  return (files ?? []).map(file => {
    const identity = [
      file.source,
      file.field,
      file.step_label,
      file.display_name,
    ].join('|');
    const repeat = seen.get(identity) ?? 0;
    seen.set(identity, repeat + 1);
    return { file, key: `${identity}|${repeat}` };
  });
}

/**
 * The compact label for a run that matched inside its files: "Matched in
 * invoice.pdf" for one file, "Matched in 3 files" otherwise. A file listed
 * twice (an input a step also read) still counts as one name.
 *
 * @param match - A run's content match
 * @returns The label, or undefined when the match lists no files
 */
export function getContentMatchSummary(
  match: V1RunContentMatch | undefined,
): string | undefined {
  const files = match?.file_matches ?? [];
  if (!match || files.length === 0) return undefined;
  const names = new Set(files.map(getFileMatchName));
  if (names.size === 1 && getHiddenFileMatchCount(match) === 0) {
    return t('automation.runs.contentSearch.matchedIn', {
      name: [...names][0],
    });
  }
  const count = Math.max(match.file_match_count ?? 0, files.length);
  return t('automation.runs.contentSearch.matchedInFiles', { count });
}

const SKIP_REASON_LABEL_KEYS: Record<string, string> = {
  [V1ContentSkipReason.CONTENT_SKIP_REASON_NO_TEXT_LAYER]:
    'automation.runs.contentSearch.skipReason.noTextLayer',
  [V1ContentSkipReason.CONTENT_SKIP_REASON_TOO_LARGE]:
    'automation.runs.contentSearch.skipReason.tooLarge',
  [V1ContentSkipReason.CONTENT_SKIP_REASON_UNSUPPORTED_FORMAT]:
    'automation.runs.contentSearch.skipReason.unsupportedFormat',
  [V1ContentSkipReason.CONTENT_SKIP_REASON_ENCRYPTED]:
    'automation.runs.contentSearch.skipReason.encrypted',
  [V1ContentSkipReason.CONTENT_SKIP_REASON_FAILED]:
    'automation.runs.contentSearch.skipReason.failed',
  [V1ContentSkipReason.CONTENT_SKIP_REASON_TOO_MANY_FILES]:
    'automation.runs.contentSearch.skipReason.tooManyFiles',
  [V1ContentSkipReason.CONTENT_SKIP_REASON_INACCESSIBLE]:
    'automation.runs.contentSearch.skipReason.inaccessible',
};

/** One line of a coverage breakdown: why some of a run's files can't be searched. */
export interface IContentCoverageLine {
  key: string;
  label: string;
  count: number;
}

/** A run's file coverage, reduced to what the runs list shows. */
export interface IContentCoverageSummary {
  /** "2 of 3 files searchable" */
  label: string;
  /** Pending, partly searchable, then skipped files by reason. */
  lines: IContentCoverageLine[];
}

/**
 * Summarizes which of a run's files content search can read. Returns nothing
 * when every file is fully searchable, so only runs a search could miss carry
 * a note.
 *
 * @param coverage - The run's coverage; unset for Drafts and orgs without content search
 * @returns The note and its breakdown, or undefined when there is nothing to flag
 */
export function getContentCoverageSummary(
  coverage: V1RunContentCoverage | undefined,
): IContentCoverageSummary | undefined {
  const total = coverage?.file_count ?? 0;
  if (!coverage || total <= 0) return undefined;
  const searchable = coverage.indexed_file_count ?? 0;
  const partial = coverage.partially_indexed_file_count ?? 0;
  const pending = coverage.pending_file_count ?? 0;
  if (searchable >= total && partial <= 0) return undefined;

  const lines: IContentCoverageLine[] = [];
  if (pending > 0) {
    lines.push({
      key: 'pending',
      label: t('automation.runs.contentSearch.coverage.pending'),
      count: pending,
    });
  }
  if (partial > 0) {
    lines.push({
      key: 'partial',
      label: t('automation.runs.contentSearch.coverage.partial'),
      count: partial,
    });
  }
  // Reasons this build doesn't know are summed under one generic line.
  let unknownSkipped = 0;
  for (const skipped of coverage.skipped_file_counts ?? []) {
    const count = skipped.file_count ?? 0;
    if (count <= 0) continue;
    const key = skipped.reason
      ? SKIP_REASON_LABEL_KEYS[skipped.reason]
      : undefined;
    if (key) {
      lines.push({ key: skipped.reason!, label: t(key), count });
    } else {
      unknownSkipped += count;
    }
  }
  if (unknownSkipped > 0) {
    lines.push({
      key: 'unknown',
      label: t('automation.runs.contentSearch.skipReason.unknown'),
      count: unknownSkipped,
    });
  }

  return {
    label:
      searchable < total
        ? t('automation.runs.contentSearch.coverage.summary', {
            searchable,
            count: total,
          })
        : t('automation.runs.contentSearch.coverage.partialSummary', {
            count: partial,
          }),
    lines,
  };
}

/** A search the server refused for reading file contents, with what to tell the user. */
export type TContentSearchError =
  | { kind: 'rateLimited'; retryAfterSeconds?: number }
  | { kind: 'timedOut' };

const RETRY_IN_PATTERN = /retry in (\d+)\s*s\b/i;

/**
 * Reads the wait the server asked for, from jeeves' "retry in N s" message or
 * a `Retry-After` header in seconds.
 */
function getRetryAfterSeconds(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined;
  const message = error.response?.data?.message;
  const fromMessage =
    typeof message === 'string' ? message.match(RETRY_IN_PATTERN)?.[1] : null;
  const header = error.response?.headers?.['retry-after'];
  const seconds = Number(fromMessage ?? header);
  return Number.isFinite(seconds) && seconds > 0
    ? Math.ceil(seconds)
    : undefined;
}

/**
 * Classifies a failed ListRuns call made with a search term. A search that
 * reads file contents is rate limited per user (429) and stopped past a
 * statement timeout (504); both deserve a hint rather than a generic error.
 *
 * @param error - The query error
 * @returns The kind of refusal, or undefined for any other error
 */
export function getContentSearchError(
  error: unknown,
): TContentSearchError | undefined {
  if (!isAxiosError(error)) return undefined;
  const status = error.response?.status ?? error.status;
  if (status === 429) {
    return {
      kind: 'rateLimited',
      retryAfterSeconds: getRetryAfterSeconds(error),
    };
  }
  if (status === 504) return { kind: 'timedOut' };
  return undefined;
}

/**
 * Whether a run has anything to show in the runs list's content search
 * details, so a cell without it renders exactly as it did before content search.
 *
 * @param run - The run
 * @param match - The run's entry in ListRuns `content_matches`, if any
 * @returns True when there is a match indicator or a coverage note to show
 */
export function hasRunContentSearchDetails(
  run: V1Run | undefined,
  match: V1RunContentMatch | undefined,
): boolean {
  return (
    Boolean(getContentMatchSummary(match)) ||
    Boolean(getContentCoverageSummary(run?.content_coverage))
  );
}
